import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PillarArticle, articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { buildHead, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import cover from "@/assets/cover-pillar-migration-es.jpg";
import cover640 from "@/assets/cover-pillar-migration-es-640.webp";
import cover960 from "@/assets/cover-pillar-migration-es-960.webp";
import cover1280 from "@/assets/cover-pillar-migration-es-1280.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const COVER_SRCSET = `${cover640} 640w, ${cover960} 960w, ${cover1280} 1280w`;
const COVER_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";

const PATH = "/es/recursos/playbook-replatforming-shopify";
const ALT = "/resources/shopify-replatforming-playbook";
const TITLE = "Replatforming a Shopify: cutover y SEO · Shugert";
const DESC =
  "Ejecuta un replatform a Shopify con migración de datos, continuidad SEO, integraciones, cutover, rollback y estabilización de 90 días.";
const PUBLISHED = "2026-05-19T09:00:00.000Z";
const MODIFIED = "2026-08-17T11:00:00.000Z";

const SECTIONS = [
  {
    id: "modelo-operativo-replatforming",
    heading: "Modelo operativo para replatforming a Shopify",
    blocks: [
      "Un replatforming a Shopify es un programa gobernado para reemplazar una plataforma ecommerce mientras se protegen revenue, integridad de datos, discoverability y operaciones. Esta sección define la evidencia necesaria desde discovery hasta estabilización.",
      "### Evidencia requerida en cada gate",
      "- Discovery: scope, exclusiones, sistemas, owners, riesgos y fuentes de verdad aprobadas.",
      "- Prueba de datos: contrato de mapeo, excepciones, totales conciliados y muestra representativa.",
      "- Build e integración: journeys críticos y pruebas de fallo, retry y recovery.",
      "- Ensayo de cutover: runbook cronometrado, comunicación, rollback y autoridad go/no-go.",
      "- Estabilización: umbrales, triage, backlog y criterios de salida.",
      "### ¿En qué se diferencia de la guía de migración?",
      "La [guía de migración a Shopify](/es/recursos/shopify-migration-guide-2026) define qué cubre una migración completa. Este playbook cubre gobierno, cutover y estabilización. La [guía de Shopify Plus](/es/recursos/shopify-plus-migration) cubre calificación, costos y riesgos específicos de Plus.",
    ],
  },
  {
    id: "por-que-replatform",
    heading: "Por qué las marcas hacen replatforming a Shopify",
    blocks: [
      "Migrar de plataforma es la cosa más cara que una marca DTC de $5M–$50M va a hacer en cinco años. Bien hecho, desbloquea 18–30 meses de velocidad compuesta. Mal hecho, mata el tráfico SEO dos trimestres y el equipo no se recupera.",
      "Las marcas que migramos a Shopify o Shopify Plus suelen compartir tres señales: la plataforma actual asfixia la velocidad de ingeniería, el checkout es un leak de ingresos conocido que el equipo no puede arreglar en la plataforma actual, y merchandising quiere ownership del cambio sin filar un ticket.",
      "Si solo tienes una de esas, optimiza lo que tienes. Si tienes las tres, esta guía es para ti.",
    ],
  },
  {
    id: "cuatro-formas-migracion",
    heading: "Las cuatro formas de migración que corremos",
    blocks: [
      "No todas las migraciones se ven igual. Mapear la tuya a una de estas cuatro formas determina presupuesto, timeline y superficie de riesgo.",
      "### Forma 1, Magento / Adobe Commerce → Shopify o Shopify Plus",
      "Build de 14–22 semanas. Lo difícil: complejidad de catálogo (configurable products → variants), jerarquías multi-store y décadas de estructura de URLs. El riesgo headline es SEO.",
      "### Forma 2, BigCommerce → Shopify o Shopify Plus",
      "Suele ser la más limpia. APIs limpias y shapes de product/customer/order mapean bien. 8–14 semanas. Lo difícil: price lists B2B, customer groups y cualquier lógica específica de Stencil.",
      "### Forma 3, WooCommerce → Shopify o Shopify Plus",
      "La data es el titular. Las tiendas WooCommerce acumulan una década de meta keys, tablas de plugins y taxonomía inconsistente. Planea 4 semanas de auditoría de data antes de estimar el build.",
      "### Forma 4, Stack custom → Shopify o Hydrogen",
      "Las más caras. El equipo suele tener opiniones fuertes y la migración necesita preservar una capa editorial o de personalización específica. Casi siempre terminamos en Hydrogen + Plus, no en Liquid.",
    ],
  },
  {
    id: "url-seo",
    heading: "Estrategia de URL & SEO: el plan de 301s que preserva rankings",
    blocks: [
      "Cada migración que auditamos que perdió tráfico orgánico lo perdió en la capa de URLs. Shopify fuerza patrones específicos (/products/, /collections/) que casi seguro no matchean tu sitio actual. Sin plan, el tráfico orgánico cae 30–60% en los primeros 30 días.",
      "### Lo no negociable",
      "- Crawl del sitio actual con Screaming Frog y exportar cada URL indexada con rank, tráfico y backlinks.",
      "- Spreadsheet 1:1 de URL vieja → URL Shopify nueva. Cada URL con tráfico o backlinks tiene que tener target.",
      "- Implementar redirects en la tabla de URL Redirects de Shopify (o Cloudflare Workers enfrente) antes del cutover de DNS.",
      "- Evitar cadenas de redirect. Vieja → nueva en un hop, nunca dos.",
      "### Cosas que muerden",
      "URLs de colecciones faceteadas, tag pages del blog, URLs internacionalizadas y páginas de resultados de búsqueda necesitan política. Decide cuáles redirigir, cuáles canonicalizar y cuáles dejar 404. Documenta antes del cutover, no después.",
    ],
  },
  {
    id: "data-migration",
    heading: "Migración de datos: productos, clientes, órdenes, suscripciones",
    blocks: [
      "El spreadsheet de migración tiene más filas que el build. Movemos típicamente 6 shapes de data y cada uno tiene trampas.",
      "### Productos y variantes",
      "Shopify limita variantes a 100 por producto. Si tienes configurable products que explotan más allá, necesitas bundles, metafields o splits. Decide antes del build.",
      "### Clientes",
      "Los password hashes no se transfieren. Planea un flujo de password reset con warmup de email para no disparar filtros de spam con un millón de resets el día 1.",
      "### Órdenes",
      "Órdenes históricas pueden importarse vía Bulk Operations API para referencia. Pero normalmente no quieres refunds, returns o renovaciones de suscripción corriendo contra registros importados, mantén históricos read-only.",
      "### Suscripciones",
      "Recharge, Skio y Stay AI tienen rutas de migración, pero cada uno requiere re-tokenización de vault. Agenda con tu processor 6 semanas antes.",
      "### Metafields & data custom",
      "Aquí desaparece la semana lenta. Data custom raramente tiene equivalente limpio en Shopify. Mapéala a metafields con namespaces que controles tú, no el namespace de la app.",
    ],
  },
  {
    id: "inventario-apps",
    heading: "Inventario de apps e integraciones: cortar, reemplazar, conservar",
    blocks: [
      "Cada tienda que migramos tiene al menos una app que existe porque alguien, hace 4 años, necesitaba algo para una campaña. La migración es el momento correcto para cortar.",
      "### Auditoría",
      "Lista cada app, cada webhook y cada consumer de API. Para cada uno: business owner, job-to-be-done y costo mensual. Luego ordena en tres columnas: Conservar (esencial), Reemplazar (Shopify tiene nativo o mejor), Cortar (ya no se necesita).",
      "### Cortes comunes",
      "Motores de recomendación custom (Shopify tiene Search & Discovery), apps legacy de reviews, herramientas legacy de CRO que no soportan el nuevo checkout. La mayoría de tiendas corta 20–30% del stack en la migración.",
      "### Conservas comunes",
      "Klaviyo, Gorgias, el processor de suscripciones y la integración con ERP/3PL. Plan esas conexiones durante el build, no después.",
    ],
  },
  {
    id: "cutover",
    heading: "El cutover: staging, freeze windows, DNS, rollback",
    blocks: [
      "El cutover es el único paso que el equipo ejecutivo va a recordar. Planéalo como un launch, no como un deploy.",
      "### Timeline",
      "- T-14 días: feature freeze en la plataforma vieja. Sin contenido nuevo, sin productos nuevos, sin código nuevo.",
      "- T-7 días: ensayo final de data sync. Cronométralo. Documenta cada paso.",
      "- T-3 días: pausar marketing pago a landings de alto tráfico.",
      "- T-0: cutover en la ventana de menor tráfico (usualmente 2–6am de la tienda, en martes).",
      "- T+24h: war room 24 horas. Ingeniería, marketing, soporte y agencia en Slack compartido.",
      "### Rollback",
      "Mantener la plataforma vieja caliente 14 días. TTLs bajos. Si surge algo catastrófico en las primeras 48 horas, quieres poder revertir.",
    ],
  },
  {
    id: "post-launch",
    heading: "Post-launch: monitoreo, performance, el 30/60/90",
    blocks: [
      "Las marcas que salen felices de un replatform no son las que clavaron el día de launch, son las que clavaron la semana 4.",
      "### Días 0–30",
      "Monitorear todo. Conversión, AOV, RPS, tráfico orgánico y Core Web Vitals contra baseline pre-launch. Cachar regresiones en días, no semanas.",
      "### Días 30–60",
      "Enviar los items que conscientemente pateaste del scope. La mayoría de tiendas tiene 8–15 \"phase 2 items\". Conviértelo en backlog de 60 días.",
      "### Días 60–90",
      "Arrancar la capa de optimización. Ya en Plus tienes acceso a Functions, Markets y checkout extensibility. Envía una victoria por área en el primer trimestre post-launch.",
    ],
  },
  {
    id: "costos",
    heading: "Controles de presupuesto para un replatform enterprise a Shopify",
    blocks: [
      "No hay respuestas limpias, pero sí rangos. Trata a quien te cotice sin discovery como vendor, no como partner.",
      "### Costo de build",
      "La mayoría de BigCommerce → Plus aterriza en $45k–$120k. Magento y stacks custom corren $90k–$300k+. Builds de Hydrogen suman 30–60% encima.",
      "### Costo interno",
      "Planea 0.5–1.0 FTE del lado de la marca durante todo el proceso: alguien que pueda contestar dudas dentro del mismo día hábil. Las migraciones se enlentecen cuando las respuestas del cliente toman una semana.",
      "### Costo de tiempo",
      "12–24 semanas end to end. Otras 8 semanas de estabilización. No agendes un relaunch de marca la misma semana del launch de plataforma, tu equipo va a estar agotado.",
    ],
  },
];

const FAQS = [
  {
    q: "¿La migración va a afectar mi SEO?",
    a: "No si planeas la estrategia de URLs antes de que arranque código. Hacemos crawl del sitio actual, armamos mapa 1:1 de URLs, enviamos 301s antes del cutover de DNS y monitoreamos tráfico orgánico diario los primeros 30 días. La mayoría de nuestras migraciones recupera o mejora orgánico en 60 días.",
  },
  {
    q: "¿Cuánto dura una migración típica a Shopify Plus?",
    a: "8–14 semanas desde BigCommerce, 14–22 desde Magento o stack custom, más 4–8 semanas de estabilización. Catálogos grandes, B2B y stacks de suscripción suben al rango alto.",
  },
  {
    q: "¿Podemos mantener las suscripciones vivas durante la migración?",
    a: "Sí. Agendamos re-tokenización de vault con tu processor (Recharge, Skio, Stay AI) ~6 semanas antes del cutover. Los flujos al subscriber siguen vivos; el cambio es server-side.",
  },
  {
    q: "¿Necesitamos Plus o Shopify estándar es suficiente?",
    a: "Si estás bajo $2M ARR y no corres B2B, Markets o Functions, Shopify estándar suele bastar. Plus se paga solo cuando necesitas checkout extensibility, wholesale avanzado o multi-storefront.",
  },
  {
    q: "¿Cuál es el error más grande en migraciones?",
    a: "Saltar la auditoría de data. Equipos asumen que la data de la plataforma vieja está limpia, arrancan el build, y a la tercera semana descubren que 12% de los productos tienen variant data rota. Siempre haz 1–2 semanas de auditoría de data antes de fijar el estimado del build.",
  },
];

const RELATED = [
  { label: "Guía: planeación neutral de migración a Shopify", href: "/es/recursos/shopify-migration-guide-2026" },
  { label: "Guía: cuándo se justifica Shopify Plus", href: "/es/recursos/shopify-plus-migration" },
  { label: "Guía: cronograma de migración a Shopify", href: "/es/recursos/shopify-migration-timeline" },
  { label: "Pilar: migrar de WooCommerce a Shopify sin perder SEO", href: "/es/recursos/migracion-woocommerce-shopify" },
  { label: "Ver todas las guías de migración →", href: "/es/recursos/migracion" },
  { label: "Servicio: migración a Shopify", href: "/es/migracion-shopify" },
  { label: "Pilar: agencia Shopify Plus", href: "/es/agencia-shopify-plus" },
];

export const Route = createFileRoute("/es/recursos/playbook-replatforming-shopify")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "es",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: cover,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Migración",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({ image: cover,
          title: TITLE,
          description: DESC,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Migración",
        }),
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Recursos", path: "/es/recursos" },
          { name: "Migración", path: "/es/recursos/migracion" },
          { name: "Playbook de replatforming", path: PATH },
        ]),
        faqJsonLd(FAQS),
      ],
    }),
  component: () => (
    <SiteShell>
      <PillarArticle
        locale="es"
        path={PATH}
        eyebrow="Migración"
        title="Playbook de replatforming a Shopify: cutover y estabilización de 90 días"
        intro="Un playbook de ejecución para migrar desde WooCommerce, Magento, BigCommerce o una plataforma propia a Shopify o Shopify Plus: datos, continuidad SEO, integraciones, cutover, rollback y estabilización."
        updated="Agosto 2026"
        readingMinutes={20}
        hubHref="/es/recursos/migracion"
        hubLabel="Ver todas las guías de migración"
        sections={SECTIONS}
        cover={{ src: cover, srcSet: COVER_SRCSET, sizes: COVER_SIZES, alt: "Playbook de replatforming a Shopify, portada ilustrada de cebra" }}
        midCTA={{
          eyebrow: "Antes de comprometerte",
          title: "Pide una auditoría de feasibility de migración",
          body: "Dos semanas de ingeniería senior y estrategia que mapean tu catálogo, data, URLs, integraciones y superficie de riesgo, antes de firmar un SOW de build con nadie.",
          href: "/es/auditoria",
          label: "Ver auditoría",
        }}
        finalCTA={{
          eyebrow: "Listo para cotizar",
          title: "Haz replatforming a Shopify con un equipo senior",
          body: "Cuéntanos tu plataforma actual, tamaño de catálogo y los sistemas que no puedes perder. Te mandamos un plan de migración de alcance fijo con timeline, milestones y precio.",
          href: "/es/cotizacion?source=pillar-migration",
          label: "Cotizar migración",
        }}
        faqs={FAQS}
        related={RELATED}
        publishedTime={PUBLISHED}
        modifiedTime={MODIFIED}
      />
    </SiteShell>
  ),
});